import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Icon, Tooltip, Modal, Radio, DatePicker as AntDatePicker } from "antd";
import {
    Container,
    NavigationContainer,
    NavArrow,
    MonthDisplay,
    Dropdown,
    Header,
    NavButton,
    YearDisplay,
    YearText,
    MonthGrid,
    MonthCell,
    Footer,
    FooterButton,
    Overlay,
    TodayButton,
} from "./styles";

var AntMonthPicker = AntDatePicker.MonthPicker;

var MONTHS = [
    "Janeiro", "Fevereiro", "Março", "Abril",
    "Maio", "Junho", "Julho", "Agosto",
    "Setembro", "Outubro", "Novembro", "Dezembro"
];

/**
 * MonthPicker — Month selector with optional custom date range support.
 *
 * See docs/ADVANCED_FILTERS.md for full patterns and best practices.
 */
var MonthPicker = function(props) {
    var value = props.value;
    var onChange = props.onChange;
    var format = props.format || "YYYY-MM";
    var displayFormat = props.displayFormat || "MMMM [de] YYYY";
    var placeholder = props.placeholder || "Selecione o mês";
    var disabled = props.disabled;
    var minDate = props.minDate;
    var maxDate = props.maxDate;
    var align = props.align || "left";
    var showFooter = props.showFooter !== false;
    var showTodayButton = props.showTodayButton === true;
    var onCustomRange = props.onCustomRange;
    var customRangeLabel = props.customRangeLabel;

    var containerRef = useRef(null);
    var [isOpen, setIsOpen] = useState(false);
    var [rangeModalOpen, setRangeModalOpen] = useState(false);
    var [rangeFrom, setRangeFrom] = useState(null);
    var [rangeTo, setRangeTo] = useState(null);
    // No período personalizado: escolher por mês (jan–jun) ou por data (05/02–15/04).
    var [rangeUnit, setRangeUnit] = useState('day');

    var changeRangeUnit = function(unit) {
        setRangeUnit(unit);
        if (unit === 'month') {
            if (rangeFrom) setRangeFrom(rangeFrom.clone().startOf('month'));
            if (rangeTo) setRangeTo(rangeTo.clone().endOf('month'));
        }
    };
    var [viewYear, setViewYear] = useState(function() {
        if (value) {
            return moment(value, format).year();
        }
        return moment().year();
    });

    var currentMoment = value ? moment(value, format) : null;
    var todayMoment = moment();

    var isCurrentMonth = currentMoment && currentMoment.isSame(todayMoment, 'month');

    useEffect(function() {
        if (value) {
            setViewYear(moment(value, format).year());
        }
    }, [value, format]);

    var handleToggle = function(e) {
        e.stopPropagation();
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    var handleClose = function() {
        setIsOpen(false);
    };

    var handlePrevMonth = function(e) {
        e.stopPropagation();
        if (!disabled && currentMoment) {
            var newDate = currentMoment.clone().subtract(1, 'month');
            if (onChange) {
                onChange(newDate.format(format), newDate);
            }
        }
    };

    var handleNextMonth = function(e) {
        e.stopPropagation();
        if (!disabled && currentMoment) {
            var newDate = currentMoment.clone().add(1, 'month');
            if (onChange) {
                onChange(newDate.format(format), newDate);
            }
        }
    };

    var handlePrevYear = function() {
        setViewYear(viewYear - 1);
    };

    var handleNextYear = function() {
        setViewYear(viewYear + 1);
    };

    var handleSelectMonth = function(monthIndex) {
        var selectedDate = moment().year(viewYear).month(monthIndex).startOf("month");

        if (isMonthDisabled(monthIndex)) {
            return;
        }

        if (onChange) {
            onChange(selectedDate.format(format), selectedDate);
        }
        setIsOpen(false);
    };

    var handleToday = function(e) {
        if (e) e.stopPropagation();
        var today = moment();
        setViewYear(today.year());
        if (onChange) {
            onChange(today.format(format), today);
        }
        setIsOpen(false);
    };

    var handleClear = function() {
        if (onChange) {
            onChange(null, null);
        }
        setIsOpen(false);
    };

    var isMonthDisabled = function(monthIndex) {
        var date = moment().year(viewYear).month(monthIndex);

        if (minDate) {
            var min = moment(minDate, format);
            if (date.isBefore(min, "month")) {
                return true;
            }
        }

        if (maxDate) {
            var max = moment(maxDate, format);
            if (date.isAfter(max, "month")) {
                return true;
            }
        }

        return false;
    };

    var isMonthSelected = function(monthIndex) {
        if (!currentMoment) return false;
        return currentMoment.year() === viewYear && currentMoment.month() === monthIndex;
    };

    var isMonthCurrent = function(monthIndex) {
        return todayMoment.year() === viewYear && todayMoment.month() === monthIndex;
    };

    var getDisplayText = function() {
        if (customRangeLabel) {
            return customRangeLabel;
        }
        if (!currentMoment) {
            return placeholder;
        }
        var text = currentMoment.format(displayFormat);
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    var displayText = getDisplayText();

    return (
        <Container ref={containerRef} style={props.style}>
            <NavigationContainer disabled={disabled} style={{ width: '100%' }}>
                <Tooltip title="Mês anterior">
                    <NavArrow onClick={handlePrevMonth} disabled={disabled}>
                        <Icon type="left" />
                    </NavArrow>
                </Tooltip>

                <MonthDisplay onClick={handleToggle} isOpen={isOpen} disabled={disabled} style={{ flex: 1 }}>
                    {displayText}
                </MonthDisplay>

                <Tooltip title="Próximo mês">
                    <NavArrow onClick={handleNextMonth} disabled={disabled}>
                        <Icon type="right" />
                    </NavArrow>
                </Tooltip>

                {showTodayButton && (
                    <Tooltip title="Ir para mês atual">
                        <TodayButton onClick={handleToday} isActive={isCurrentMonth} disabled={disabled}>
                            Atual
                        </TodayButton>
                    </Tooltip>
                )}
            </NavigationContainer>

            {isOpen && (
                <>
                    <Overlay onClick={handleClose} />
                    <Dropdown align={align}>
                        <Header>
                            <NavButton onClick={handlePrevYear} type="button">
                                <Icon type="double-left" />
                            </NavButton>
                            <YearDisplay>
                                <NavButton onClick={handlePrevYear} type="button">
                                    <Icon type="left" />
                                </NavButton>
                                <YearText>{viewYear}</YearText>
                                <NavButton onClick={handleNextYear} type="button">
                                    <Icon type="right" />
                                </NavButton>
                            </YearDisplay>
                            <NavButton onClick={handleNextYear} type="button">
                                <Icon type="double-right" />
                            </NavButton>
                        </Header>

                        <MonthGrid>
                            {MONTHS.map(function(month, index) {
                                return (
                                    <MonthCell
                                        key={month}
                                        type="button"
                                        onClick={function() { handleSelectMonth(index); }}
                                        isSelected={isMonthSelected(index)}
                                        isCurrent={isMonthCurrent(index)}
                                        disabled={isMonthDisabled(index)}
                                    >
                                        {month.substring(0, 3)}
                                    </MonthCell>
                                );
                            })}
                        </MonthGrid>

                        {/* Quick presets */}
                        {onCustomRange && (
                            <div style={{ padding: '6px 14px 2px', borderTop: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: 10, color: '#bfbfbf', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, fontWeight: 600 }}>Atalhos</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                                    {[
                                        { label: 'Hoje', from: moment().startOf('day'), to: moment().endOf('day') },
                                        { label: 'Esta semana', from: moment().startOf('week'), to: moment().endOf('week') },
                                        { label: 'Este mês', from: moment().startOf('month'), to: moment().endOf('month') },
                                        { label: 'Este ano', from: moment().startOf('year'), to: moment().endOf('year') },
                                        { label: 'Últ. 30 dias', from: moment().subtract(30, 'days'), to: moment() },
                                        { label: 'Últ. 3 meses', from: moment().subtract(3, 'months').startOf('month'), to: moment().endOf('month') },
                                        { label: 'Últ. 6 meses', from: moment().subtract(6, 'months').startOf('month'), to: moment().endOf('month') },
                                        { label: 'Últ. 12 meses', from: moment().subtract(12, 'months').startOf('month'), to: moment().endOf('month') },
                                        { label: 'Personalizado', custom: true },
                                        { label: 'Mês Atual', today: true },
                                    ].map(function(preset) {
                                        return (
                                            <button key={preset.label} type="button"
                                                onClick={function() {
                                                    if (preset.custom) {
                                                        setIsOpen(false);
                                                        setRangeModalOpen(true);
                                                    } else if (preset.today) {
                                                        handleToday();
                                                    } else {
                                                        onCustomRange(preset.from.format('YYYY-MM-DD'), preset.to.format('YYYY-MM-DD'));
                                                        setIsOpen(false);
                                                    }
                                                }}
                                                style={{
                                                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                                                    border: '1px solid ' + (preset.custom && customRangeLabel ? '#722ed1' : '#e8e8e8'),
                                                    background: preset.custom && customRangeLabel ? '#f9f0ff' : '#fff',
                                                    color: preset.custom ? '#722ed1' : '#595959',
                                                    fontWeight: preset.custom || preset.today ? 500 : 400,
                                                    transition: 'all 0.15s', lineHeight: '18px',
                                                }}
                                                onMouseEnter={function(e) { e.currentTarget.style.borderColor = preset.custom ? '#722ed1' : '#1890ff'; e.currentTarget.style.color = preset.custom ? '#722ed1' : '#1890ff'; e.currentTarget.style.background = preset.custom ? '#f9f0ff' : '#e6f7ff'; }}
                                                onMouseLeave={function(e) { e.currentTarget.style.borderColor = preset.custom && customRangeLabel ? '#722ed1' : '#e8e8e8'; e.currentTarget.style.color = preset.custom ? '#722ed1' : '#595959'; e.currentTarget.style.background = preset.custom && customRangeLabel ? '#f9f0ff' : '#fff'; }}
                                            >
                                                {preset.custom && <Icon type="edit" style={{ marginRight: 3, fontSize: 10 }} />}
                                                {preset.today && <Icon type="clock-circle" style={{ marginRight: 3, fontSize: 10 }} />}
                                                {preset.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {showFooter && !onCustomRange && (
                            <Footer>
                                <FooterButton type="button" onClick={handleClear}>
                                    Limpar
                                </FooterButton>
                                <FooterButton type="button" onClick={handleToday}>
                                    Mês Atual
                                </FooterButton>
                            </Footer>
                        )}
                    </Dropdown>
                </>
            )}
            {onCustomRange && (
                <Modal
                    title={<span><Icon type="calendar" style={{ marginRight: 8, color: '#722ed1' }} />Período personalizado</span>}
                    visible={rangeModalOpen}
                    onCancel={function() { setRangeModalOpen(false); }}
                    onOk={function() {
                        if (rangeFrom && rangeTo) {
                            var unit = rangeUnit === 'month' ? 'month' : 'day';
                            var swap = rangeFrom.isAfter(rangeTo, unit);
                            var from = (swap ? rangeTo : rangeFrom).clone().startOf(unit);
                            var to = (swap ? rangeFrom : rangeTo).clone().endOf(unit);
                            onCustomRange(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'));
                            setRangeModalOpen(false);
                        }
                    }}
                    okText="Aplicar"
                    cancelText="Cancelar"
                    okButtonProps={{ disabled: !rangeFrom || !rangeTo }}
                    width={400}
                    destroyOnClose
                    zIndex={9999}
                >
                    <Radio.Group
                        size="small"
                        value={rangeUnit}
                        onChange={function(e) { changeRangeUnit(e.target.value); }}
                        style={{ display: 'flex', marginBottom: 12 }}
                    >
                        <Radio.Button value="month" style={{ flex: 1, textAlign: 'center' }}>Por mês</Radio.Button>
                        <Radio.Button value="day" style={{ flex: 1, textAlign: 'center' }}>Por data</Radio.Button>
                    </Radio.Group>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>De</div>
                            {rangeUnit === 'month' ? (
                                <AntMonthPicker
                                    key="from-month"
                                    value={rangeFrom}
                                    onChange={function(d) { setRangeFrom(d ? d.clone().startOf('month') : d); }}
                                    format="MMM/YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="Mês início"
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            ) : (
                                <AntDatePicker
                                    key="from-day"
                                    value={rangeFrom}
                                    onChange={function(d) { setRangeFrom(d); }}
                                    format="DD/MM/YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="Data início"
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>Até</div>
                            {rangeUnit === 'month' ? (
                                <AntMonthPicker
                                    key="to-month"
                                    value={rangeTo}
                                    onChange={function(d) { setRangeTo(d ? d.clone().endOf('month') : d); }}
                                    format="MMM/YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="Mês fim"
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            ) : (
                                <AntDatePicker
                                    key="to-day"
                                    value={rangeTo}
                                    onChange={function(d) { setRangeTo(d); }}
                                    format="DD/MM/YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="Data fim"
                                    getCalendarContainer={function(trigger) { return trigger.parentNode; }}
                                />
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 16 }}>
                        {[
                            { label: 'Últimos 3 meses', from: moment().subtract(3, 'months').startOf('month'), to: moment().endOf('month') },
                            { label: 'Últimos 6 meses', from: moment().subtract(6, 'months').startOf('month'), to: moment().endOf('month') },
                            { label: 'Este ano', from: moment().startOf('year'), to: moment().endOf('year') },
                            { label: 'Ano passado', from: moment().subtract(1, 'year').startOf('year'), to: moment().subtract(1, 'year').endOf('year') },
                        ].map(function(preset) {
                            return (
                                <button key={preset.label} type="button"
                                    onClick={function() { setRangeFrom(preset.from); setRangeTo(preset.to); }}
                                    style={{
                                        padding: '4px 12px', borderRadius: 16, fontSize: 12, cursor: 'pointer',
                                        border: '1px solid #d9d9d9', background: '#fafafa', color: '#595959',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={function(e) { e.currentTarget.style.borderColor = '#722ed1'; e.currentTarget.style.color = '#722ed1'; }}
                                    onMouseLeave={function(e) { e.currentTarget.style.borderColor = '#d9d9d9'; e.currentTarget.style.color = '#595959'; }}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                    </div>
                </Modal>
            )}
        </Container>
    );
};

export default MonthPicker;
